import Button from '@atlaskit/button/new';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import ClockIcon from '@atlaskit/icon/core/clock';
import LocationIcon from '@atlaskit/icon/core/location';
import StoryIcon from '@atlaskit/icon/core/story';
import { Flex, Stack } from '@atlaskit/primitives/compiled';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import { Heading, PopupShare, Text } from '@atlassian/learning-components';
import { css } from '@compiled/react';
import { useNavigate } from 'react-router-dom';

import { useGetSavedClasses } from 'src/api/hooks/useGetSavedClasses';
import { useSaveClass } from 'src/api/hooks/useSaveClass';
import { formatDurationFromSeconds } from 'src/pages/ClassPages/utils/formatDuration';

interface Props {
  title: string;
  duration?: number;
  location: string;
  products: string[];
  courseId: string;
}

export const UpperSection = ({ title, duration, location, products, courseId }: Props) => {
  const navigate = useNavigate();
  const { data: savedClassesData } = useGetSavedClasses();
  const { mutate: saveClass, isPending: isSaving } = useSaveClass();

  const isSaved = savedClassesData?.savedIds.some((item) => item.liveTrainingId === courseId) ?? false;

  const onBackClick = () => navigate(-1);
  const onSaveClick = () => {
    saveClass({ liveTrainingId: courseId, isSaved: !isSaved });
  };

  return (
    <div css={headerContainerStyles}>
      <Flex gap="space.200" alignItems="center" justifyContent="space-between">
        <Button
          iconBefore={(iconProps) => <ArrowLeftIcon {...iconProps} color={token('color.text.brand')} />}
          appearance="subtle"
          onClick={onBackClick}
        >
          <span css={buttonTextStyles}>Back to class calendar</span>
        </Button>
        <Flex alignItems="center" gap="space.100">
          <Button
            iconAfter={(iconProps) => <StoryIcon {...iconProps} color={token('color.text.brand')} />}
            appearance="subtle"
            onClick={onSaveClick}
            isDisabled={isSaving}
          >
            <span css={buttonTextStyles}>{isSaved ? 'Saved' : 'Save class'}</span>
          </Button>
          <PopupShare urlToShare={window.location.href} buttonText="Share" color="primary" fullHeight />
        </Flex>
      </Flex>
      <Flex gap="space.100" alignItems="center">
        <Heading level={0}>{title}</Heading>
      </Flex>
      <div css={metaInfoStyles}>
        <div css={metaInfoItemStyles}>
          <Flex alignItems="start" gap="space.400">
            <Stack space="space.100">
              <Flex alignItems="center" gap="space.050">
                <ClockIcon label="duration" color={token('color.text.brand')} /> <Text size="small">DURATION</Text>
              </Flex>
              <Text size="large">{formatDurationFromSeconds(duration)}</Text>
            </Stack>
            <Stack space="space.100">
              <Flex alignItems="center" gap="space.050">
                <LocationIcon label="published" color={token('color.text.brand')} /> <Text size="small">PUBLISHED</Text>
              </Flex>
              <Text size="large">{location}</Text>
            </Stack>
          </Flex>
          <Flex alignItems="start" gap="space.400">
            <Stack space="space.100">
              <Text size="small">APPS TAUGHT</Text>
              <Flex gap="space.050">
                {products.map((product) => (
                  <div key={product} css={productsBeingTaughtStyles}>
                    {product}
                  </div>
                ))}
              </Flex>
            </Stack>
          </Flex>
        </div>
      </div>
    </div>
  );
};

const headerContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.400'),
  width: '100%',
});

const buttonTextStyles = css({
  color: token('color.text.brand'),
  textAlign: 'center',
  fontFamily: 'Charlie Text',
  fontSize: '1rem',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '1.25rem',
});

const metaInfoStyles = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: token('space.400'),

  [media.above.sm]: {
    flexDirection: 'row',
    gap: token('space.400'),
  },
});

const metaInfoItemStyles = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: token('space.400'),

  [media.above.sm]: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: token('space.400'),
  },
});

const productsBeingTaughtStyles = css({
  display: 'flex',
  width: 'fit-content',
  padding: `${token('space.075')} ${token('space.100')}`,
  justifyContent: 'center',
  borderRadius: '4px',
  border: `1px solid ${token('color.border')}`,
  background: `${token('color.background.input')}`,
  color: `${token('color.text')}`,
  textAlign: 'center',
  fontFamily: 'Charlie Text',
  fontSize: '0.75rem',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '1rem',
});
